/**
 * Shared helpers for "is this product on sale" decisions used across cards,
 * grids, and inline article chips.
 */

export interface SaleInfoInput {
  price?: number | null;
  previousPrice?: number | null;
  priceDropAt?: string | null;
  saleEndDate?: string | null;
}

export interface SaleInfo {
  isOnSale: boolean;
  percentOff: number | null;
  amountOff: number | null;
  justDropped: boolean; // dropped in the last 7 days
  saleEndsSoon: boolean; // sale_end_date within next 14 days
  saleEndsAt: string | null;
}

const JUST_DROPPED_MS = 7 * 24 * 60 * 60 * 1000;
const ENDS_SOON_MS = 14 * 24 * 60 * 60 * 1000;

export function getSaleInfo(input: SaleInfoInput): SaleInfo {
  const price = input.price != null ? Number(input.price) : null;
  const prev = input.previousPrice != null ? Number(input.previousPrice) : null;
  const drop = prev && price && prev > price;
  const percent = drop ? Math.round(((prev - price) / prev) * 100) : null;
  const amount = drop ? Math.round((prev - price) * 100) / 100 : null;

  let justDropped = false;
  if (drop && input.priceDropAt) {
    const t = new Date(input.priceDropAt).getTime();
    if (Number.isFinite(t)) justDropped = Date.now() - t < JUST_DROPPED_MS;
  }

  let saleEndsSoon = false;
  if (input.saleEndDate) {
    const t = new Date(input.saleEndDate).getTime();
    if (Number.isFinite(t)) {
      const diff = t - Date.now();
      saleEndsSoon = diff > 0 && diff < ENDS_SOON_MS;
    }
  }

  return {
    isOnSale: !!drop,
    percentOff: percent,
    amountOff: amount,
    justDropped,
    saleEndsSoon,
    saleEndsAt: input.saleEndDate || null,
  };
}

export function formatSaleEnds(saleEndDate: string): string {
  const d = new Date(saleEndDate);
  const days = Math.ceil((d.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
  if (days <= 0) return "ends today";
  if (days === 1) return "ends tomorrow";
  if (days <= 14) return `ends in ${days} days`;
  return `ends ${d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
}
